/**
 * Refund request modal — opened from the client detail page.
 *
 * Two refund modes:
 *   1. "Specific payments" — pick the token and/or approved instalments to
 *      refund, amount per payment. Refunding the token automatically promotes
 *      the next instalment to become the new token (handled server-side).
 *   2. "Custom amount" — refund any amount straight off net Total Received,
 *      not tied to a single payment (a ledger adjustment).
 *
 * Approval is admin/finance only; this just raises the request.
 *
 * Props:
 *   submission — populated FormSubmission object (the client)
 *   onClose(refreshed?) — callback. Truthy arg = caller should reload.
 */
import { useState, useMemo } from 'react';
import { AlertTriangle, FileText, Upload, Coins, ListChecks, Banknote, ArrowRightLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { ModalShell } from '../pages/admin/IncentiveDealClosures.jsx';

const REASONS = [
    { code: 'client_changed_mind',   label: 'Client changed mind' },
    { code: 'duplicate_payment',     label: 'Duplicate payment' },
    { code: 'service_not_delivered', label: 'Service not delivered' },
    { code: 'admin_decision',        label: 'Admin decision' },
    { code: 'other',                 label: 'Other' },
];

const MODES = [
    { code: 'bank_transfer', label: 'Bank transfer' },
    { code: 'upi',           label: 'UPI' },
    { code: 'cheque',        label: 'Cheque' },
    { code: 'cash',          label: 'Cash' },
];

const inr = (v) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })
        .format(Number(v || 0));

export default function RefundRequestModal({ submission, onClose }) {
    /* ── Refundable payments (token + approved, unrefunded instalments) ── */
    const refundables = useMemo(() => {
        const out = [];
        const tokenAmt = Number(submission?.data?.tokenReceivedAmount || 0);
        const tokenAlreadyRefunded = (submission?.refundRequests || [])
            .filter(r => r.status === 'approved')
            .flatMap(r => r.targets || [])
            .filter(x => x.paymentType === 'token')
            .reduce((s, x) => s + Number(x.amount || 0), 0);
        const tokenRemaining = Math.max(0, tokenAmt - tokenAlreadyRefunded);
        if (tokenRemaining > 0) {
            out.push({ key: 'token', paymentType: 'token', label: 'Token', remaining: tokenRemaining, isToken: true });
        }
        for (const p of (submission?.secondPayments || [])) {
            if (p.status !== 'approved' || p.isRefunded || p.promotedToToken) continue;
            const remaining = Number(p.amount) - Number(p.refundedAmount || 0);
            if (remaining <= 0) continue;
            out.push({
                key: String(p._id),
                paymentType: 'second',
                paymentId:   String(p._id),
                label: `Instalment · ${new Date(p.paymentDate || p.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`,
                remaining,
            });
        }
        return out;
    }, [submission]);

    /* The instalment that would be promoted to token if the token is refunded
       (earliest approved, unrefunded, not-yet-promoted). For the hint only. */
    const nextPromotable = useMemo(() => {
        const list = (submission?.secondPayments || [])
            .filter(p => p.status === 'approved' && !p.isRefunded && !p.promotedToToken)
            .sort((a, b) => new Date(a.paymentDate || a.createdAt || 0) - new Date(b.paymentDate || b.createdAt || 0));
        return list[0] || null;
    }, [submission]);

    const totalReceived = Number(submission?.totalReceived || 0);

    const [tab, setTab]           = useState('specific');   // 'specific' | 'custom'
    const [picks, setPicks]       = useState({});           // { [key]: amountString }
    const [customAmt, setCustom]  = useState('');
    const [reasonCode, setRcode]  = useState('client_changed_mind');
    const [reason, setReason]     = useState('');
    const [mode, setMode]         = useState('bank_transfer');
    const [proof, setProof]       = useState(null);
    const [busy, setBusy]         = useState(false);

    const togglePick = (k, max) => {
        setPicks(p => {
            if (p[k] !== undefined) { const n = { ...p }; delete n[k]; return n; }
            return { ...p, [k]: String(max) };
        });
    };
    const setAmount = (k, v) => setPicks(p => ({ ...p, [k]: v }));

    const totalPicked = useMemo(() =>
        Object.values(picks).reduce((s, v) => s + (Number(v) || 0), 0), [picks]);

    const overBy = useMemo(() => {
        let over = 0;
        for (const r of refundables) {
            const v = Number(picks[r.key] || 0);
            if (v > r.remaining) over += v - r.remaining;
        }
        return over;
    }, [picks, refundables]);

    const tokenPicked = picks.token !== undefined && Number(picks.token) > 0;

    /* Validity per tab */
    const customNum   = Number(customAmt) || 0;
    const customOver  = customNum > totalReceived;
    const amountValid = tab === 'specific'
        ? (totalPicked > 0 && overBy === 0)
        : (customNum > 0 && !customOver);
    const canSubmit = amountValid && reason.trim().length > 0 && !busy;

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true);
        try {
            let targets;
            if (tab === 'specific') {
                targets = refundables
                    .filter(r => picks[r.key] !== undefined && Number(picks[r.key]) > 0)
                    .map(r => ({
                        paymentType: r.paymentType,
                        paymentId:   r.paymentId || null,
                        amount:      Number(picks[r.key]),
                    }));
            } else {
                targets = [{ paymentType: 'adjustment', paymentId: null, amount: customNum }];
            }
            const fd = new FormData();
            fd.append('reason',     reason.trim());
            fd.append('reasonCode', reasonCode);
            fd.append('refundMode', mode);
            fd.append('targets',    JSON.stringify(targets));
            if (proof) fd.append('refundProof', proof);
            await api.post(`/refunds/submission/${submission._id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success('Refund request sent. Admin / finance will review.');
            onClose(true);
        } catch {/* toasted */}
        finally { setBusy(false); }
    };

    const nothingToRefund = refundables.length === 0 && totalReceived <= 0;
    if (nothingToRefund) {
        return (
            <ModalShell title="Request refund" onClose={() => onClose(false)} width="max-w-md">
                <div className="rounded-lg p-3 bg-amber-50 text-amber-800 ring-1 ring-amber-200 text-sm inline-flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>Nothing to refund on this client — no net amount has been received.</span>
                </div>
                <div className="flex justify-end pt-2">
                    <button className="ag-btn-ghost" onClick={() => onClose(false)}>Close</button>
                </div>
            </ModalShell>
        );
    }

    return (
        <ModalShell title="Request refund" onClose={() => onClose(false)} width="max-w-xl">
            {/* Net received summary */}
            <div className="rounded-lg bg-ink-50 ring-1 ring-ink-100 p-3 flex items-center justify-between text-sm">
                <span className="text-ink-600 inline-flex items-center gap-1.5"><Banknote className="h-4 w-4" /> Net Total Received</span>
                <span className="font-bold tabular-nums">{inr(totalReceived)}</span>
            </div>

            {/* Mode tabs */}
            <div className="grid grid-cols-2 gap-2 rounded-xl bg-ink-100 p-1">
                <button
                    type="button"
                    onClick={() => setTab('specific')}
                    className={`flex items-center justify-center gap-2 rounded-lg py-2 text-sm font-semibold transition
                        ${tab === 'specific' ? 'bg-white shadow text-ink-900' : 'text-ink-500 hover:text-ink-700'}`}
                >
                    <ListChecks className="h-4 w-4" /> Specific payments
                </button>
                <button
                    type="button"
                    onClick={() => setTab('custom')}
                    className={`flex items-center justify-center gap-2 rounded-lg py-2 text-sm font-semibold transition
                        ${tab === 'custom' ? 'bg-white shadow text-ink-900' : 'text-ink-500 hover:text-ink-700'}`}
                >
                    <ArrowRightLeft className="h-4 w-4" /> Custom amount
                </button>
            </div>

            {/* ── Specific payments ── */}
            {tab === 'specific' && (
                <>
                    {refundables.length === 0 ? (
                        <div className="rounded-lg p-3 bg-amber-50 text-amber-800 ring-1 ring-amber-200 text-xs">
                            No individual approved payments remain. Use “Custom amount” to refund off the net total.
                        </div>
                    ) : (
                        <div className="space-y-1">
                            <label className="ag-label">Pick payment(s) to refund *</label>
                            {refundables.map(r => {
                                const picked = picks[r.key] !== undefined;
                                const v = picks[r.key] ?? '';
                                const over = Number(v) > r.remaining;
                                return (
                                    <label key={r.key}
                                        className={`flex items-center gap-3 rounded-lg p-2 border cursor-pointer transition
                                            ${picked ? 'border-brand-teal/60 bg-brand-teal-50/60' : 'border-ink-200 bg-white hover:border-ink-300'}`}>
                                        <input type="checkbox" checked={picked}
                                            onChange={() => togglePick(r.key, r.remaining)}
                                            className="h-4 w-4 accent-brand-teal" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium inline-flex items-center gap-1.5">
                                                {r.isToken && <Coins className="h-3.5 w-3.5 text-brand-gold" />}
                                                {r.label}
                                            </div>
                                            <div className="text-[11px] text-ink-500">Available: {inr(r.remaining)}</div>
                                        </div>
                                        {picked && (
                                            <input type="number" step="0.01" min="0" max={r.remaining}
                                                className={`ag-input w-32 text-right tabular-nums ${over ? 'ring-2 ring-rose-300' : ''}`}
                                                value={v} onChange={e => setAmount(r.key, e.target.value)} />
                                        )}
                                    </label>
                                );
                            })}
                        </div>
                    )}

                    {/* Token promotion hint (scenario 1) */}
                    {tokenPicked && nextPromotable && (
                        <div className="rounded-lg p-2.5 bg-indigo-50 text-indigo-800 ring-1 ring-indigo-200 text-xs inline-flex items-start gap-2">
                            <ArrowRightLeft className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                            <span>
                                Refunding the token will automatically promote the next instalment
                                (<b>{inr(nextPromotable.amount)}</b>, {new Date(nextPromotable.paymentDate || nextPromotable.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })})
                                to become the new token.
                            </span>
                        </div>
                    )}
                    {tokenPicked && !nextPromotable && (
                        <div className="rounded-lg p-2.5 bg-amber-50 text-amber-800 ring-1 ring-amber-200 text-xs inline-flex items-start gap-2">
                            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                            <span>No remaining instalment to promote — the token will simply be cleared.</span>
                        </div>
                    )}

                    <div className={`rounded-lg p-2 text-sm flex items-center justify-between
                        ${overBy > 0 ? 'bg-rose-50 text-rose-700 ring-1 ring-rose-200' : 'bg-brand-teal-50 text-brand-teal-700'}`}>
                        <span>Total to refund</span>
                        <span className="font-bold tabular-nums">{inr(totalPicked)}</span>
                    </div>
                    {overBy > 0 && (
                        <p className="text-[11px] text-rose-700 inline-flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" /> One or more amounts exceeds what's available.
                        </p>
                    )}
                </>
            )}

            {/* ── Custom amount (scenario 3) ── */}
            {tab === 'custom' && (
                <div className="space-y-2">
                    <label className="ag-label">Refund amount *</label>
                    <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400">₹</span>
                        <input
                            type="number" step="0.01" min="0" max={totalReceived}
                            className={`ag-input pl-7 text-right tabular-nums text-lg font-bold ${customOver ? 'ring-2 ring-rose-300' : ''}`}
                            placeholder="0"
                            value={customAmt}
                            onChange={e => setCustom(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                        <span className="text-ink-500">Deducted from net Total Received (not a specific payment).</span>
                        <button type="button" className="ag-link" onClick={() => setCustom(String(totalReceived))}>
                            Max {inr(totalReceived)}
                        </button>
                    </div>
                    {customOver && (
                        <p className="text-[11px] text-rose-700 inline-flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" /> Cannot exceed net Total Received ({inr(totalReceived)}).
                        </p>
                    )}
                </div>
            )}

            {/* Reason / mode */}
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="ag-label">Reason category</label>
                    <select className="ag-input" value={reasonCode} onChange={e => setRcode(e.target.value)}>
                        {REASONS.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
                    </select>
                </div>
                <div>
                    <label className="ag-label">Refund mode *</label>
                    <select className="ag-input" value={mode} onChange={e => setMode(e.target.value)}>
                        {MODES.map(m => <option key={m.code} value={m.code}>{m.label}</option>)}
                    </select>
                </div>
            </div>

            <div>
                <label className="ag-label">Reason details *</label>
                <textarea
                    className="ag-input min-h-[80px]"
                    placeholder="Why does this refund need to happen? Admin / finance will see this verbatim."
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                />
            </div>

            <div>
                <label className="ag-label inline-flex items-center gap-1"><FileText className="h-3 w-3" /> Proof (optional)</label>
                <label className="ag-input flex items-center justify-between cursor-pointer text-xs">
                    <span className="truncate">{proof ? proof.name : 'Bank screenshot, signed cancellation letter, etc.'}</span>
                    <input type="file" className="hidden" accept="image/*,.pdf" onChange={e => setProof(e.target.files?.[0] || null)} />
                    <span className="ag-btn-ghost text-[11px] py-1 px-2"><Upload className="h-3 w-3" /> Browse</span>
                </label>
            </div>

            <div className="flex justify-end gap-2 pt-2">
                <button className="ag-btn-ghost" onClick={() => onClose(false)}>Cancel</button>
                <button className="ag-btn-primary" disabled={!canSubmit} onClick={submit}>
                    {busy ? 'Sending…' : 'Submit for approval'}
                </button>
            </div>
        </ModalShell>
    );
}
